import { Link } from '@tanstack/react-router';
import { T } from 'gt-react';
import { ArrowRightIcon, TreeStructureIcon } from '@phosphor-icons/react';
import type { TaskResponse } from '@erp/rpc';
import { Card } from 'src/components/ui/card';
import {
  StatusPill,
  PriorityDot,
} from 'src/pages/authenticated/inbox/components/helpers';
import { taskDetailSearch } from '../taskDetailNav';
import { cn } from '@/lib/utils';

/**
 * The task's direct subtasks (subtasks are full tasks — each links to its own
 * detail page). Shows each subtask's type so the list reads as the checklist
 * the stepper rolls up.
 */
export function SubtaskList({
  subtasks,
  typeNameById,
  fromPage,
}: {
  subtasks: TaskResponse[];
  typeNameById: Map<string, string>;
  fromPage?: string;
}) {
  const detailSearch = taskDetailSearch(fromPage);
  if (subtasks.length === 0) return null;

  return (
    <Card className="p-3" data-testid="subtask-list">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500">
        <TreeStructureIcon className="size-3" />
        <T context="heading for a task's subtask list">Subtasks</T>
        <span className="rounded-full bg-stone-200 px-1.5 py-px text-[10px] font-semibold text-stone-500">
          {subtasks.length}
        </span>
      </div>
      <ul className="flex flex-col">
        {subtasks.map((subtask, index) => (
          <li
            key={subtask.id}
            className={index > 0 ? 'border-t border-border' : undefined}
          >
            <Link
              to="/tasks/$taskId"
              params={{ taskId: subtask.id }}
              search={detailSearch}
              className={cn(
                'group flex items-center gap-2.5 py-2 text-sm',
                subtask.status === 'needs_input' &&
                  'rounded-md bg-[oklch(0.98_0.03_85)] px-1.5 -mx-1.5',
              )}
            >
              <PriorityDot priority={subtask.priority} />
              <span className="min-w-0 flex-1 truncate font-medium text-stone-900 group-hover:underline">
                {subtask.title}
              </span>
              {subtask.taskTypeId && (
                <span className="rounded bg-stone-100 px-1.5 py-px text-[11px] text-stone-600">
                  {typeNameById.get(subtask.taskTypeId) ?? ''}
                </span>
              )}
              {subtask.outcome && (
                <span className="rounded bg-stone-100 px-1.5 py-px text-[11px] capitalize text-stone-600">
                  {subtask.outcome}
                </span>
              )}
              <StatusPill status={subtask.status} />
              <ArrowRightIcon className="size-3.5 text-stone-400 opacity-0 transition-opacity group-hover:opacity-100" />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
